import React, { useEffect, useState } from 'react';
import { ButtonPrimary } from 'core/ui/Button';
import Box from 'core/ui/Box';
import { useTranslation } from 'react-i18next';
import Typography from 'core/ui/Typography';

import { Tooltip, IconButton, Link } from '@material-ui/core';
import { Add, Delete, Edit, FolderOpen } from '@material-ui/icons';
import LoaderSm from 'core/ui/LoaderSm';
import useSelectors from 'modules/map/useSelectors';
import useActions from 'modules/map/useActions';
import Modules from 'modules';
import { documentsModule } from 'modules/documents';
import { channelsActions } from 'modules/channels';
import { useDispatch } from 'react-redux';
import { showAPIError } from 'core/utils/showAPIError';
import Toggle from 'core/ui/Toggle';
import TextFieldRounded from 'core/ui/TextFieldRounded';
import { competitionsActions } from 'modules/competitions';
import SFDataTable from 'core/ui/DataTable';
import { gymDate } from 'core/utils/formatters';
import Divider from 'core/ui/Divider';
import ModalForm from './Form/Modal';
import ModalDocumentViewer from './ModalDocumentViewer';

const DocumentsBuilder = ({
  ownerableId,
  ownerableType,
  defaultDocumentWaiverId,
  competitionId,
}) => {
  const { t } = useTranslation();
  const [modalPreview, setModalPreview] = useState({ open: false });
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({ text: '', kind: null });
  const loading = useSelectors(documentsModule, 'loading');
  const [data, setData] = useState([]);
  const { request } = useActions();

  const [modal, setModal] = useState({
    open: false,
  });

  const getData = () => {
    request({
      action: Modules.documents.actions.getDocuments,
      data: { ownerableId, ownerableType, ...filter },
      options: {
        onSuccess: setData,
      },
    });
  };

  useEffect(() => {
    if (ownerableId && ownerableType) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, ownerableId, filter]);

  const onRemoveClick = (id) => {
    if (ownerableId && ownerableType) {
      request({
        action: Modules.documents.actions.destroy,
        data: { ownerableId, ownerableType, id },
        options: {
          onSuccess: () => {
            getData();
          },
          onError: showAPIError,
        },
      });
    }
  };

  const updatePrimaryWaiver = (docId) => {
    if (ownerableType === 'Channel' && !competitionId) {
      dispatch(
        channelsActions.putChannelStart({
          id: ownerableId,
          data: {
            id: ownerableId,
            documentWaiverId: docId,
          },
          messageSuccess: t('message.success.save.channel'),
        }),
      );
    }
    if (ownerableType === 'Competition' || competitionId) {
      dispatch(
        competitionsActions.putCompetitionStart({
          data: {
            id: competitionId,
            documentWaiverId: docId,
          },
          messageSuccess: t('message.success.save.competition'),
        }),
      );
    }
  };

  const openModal = () => {
    setModal((prev) => ({ ...prev, mode: 'new', open: true }));
  };

  const onEditClick = (item) => {
    setModal((prev) => ({ ...prev, item, mode: 'edit', open: true }));
  };

  const columns = [
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
      cell: (item) => (
        <div>
          <Typography className="tw-font-semibold tw-text-zinc-800" noWrap>
            {item?.name}
          </Typography>
        </div>
      ),
    },
    {
      name: 'Type',
      selector: 'kind',
      sortable: true,
      cell: (item) => (
        <div className="tw-flex tw-flex-row tw-items-center tw-gap-2">
          <Typography
            noWrap
            className="tw-rounded-full tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-p-1 tw-text-[10px] tw-font-semibold tw-text-gray-400"
          >
            {item.kind?.toUpperCase()}
          </Typography>
          <Typography
            noWrap
            className={`tw-rounded-full tw-border tw-border-solid tw-p-1 tw-text-[10px] tw-font-semibold ${item.pdf ? 'tw-border-blue-200 tw-bg-blue-50 tw-text-blue-400' : 'tw-border-teal-200 tw-bg-teal-50 tw-text-teal-400'}`}
          >
            {item.pdf ? 'PDF' : 'CUSTOM'}
          </Typography>
        </div>
      ),
    },

    {
      name: 'Created',
      selector: 'createdAt',
      sortable: true,
      cell: (doc) => (
        <Typography className="tw-text-xs tw-text-gray-700" noWrap>
          {doc?.createdAt && gymDate(doc?.createdAt)}
        </Typography>
      ),
    },

    {
      name: '',
      selector: '',
      sortable: false,
      right: true,
      cell: (item) => (
        <Box className="tw-flex tw-flex-row tw-items-center tw-justify-end tw-gap-2">
          {item?.id === defaultDocumentWaiverId ? (
            <Typography color="primary">{t('label.primary')}</Typography>
          ) : (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link className="tw-text-gray-700" onClick={() => updatePrimaryWaiver(item?.id)}>
              {t('button.makePrimary')}
            </Link>
          )}
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('button.view')}>
            <IconButton
              size="small"
              onClick={() => setModalPreview({ open: true, document: item })}
            >
              <FolderOpen className="tw-text-gray-700" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title={t('button.edit')}>
            <IconButton size="small" onClick={() => onEditClick(item)}>
              <Edit className="tw-text-gray-700" />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />

          <Tooltip title={t('button.delete')}>
            <IconButton size="small" onClick={() => onRemoveClick(item?.id)}>
              <Delete className="tw-text-gray-700" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {modal.open && (
        <ModalForm
          open={modal.open}
          documentId={modal.item?.id}
          mode={modal.mode}
          ownerableId={ownerableId}
          ownerableType={ownerableType}
          close={() => setModal(() => ({ open: false }))}
          callback={() => {
            setModal(() => ({ open: false }));
            getData();
          }}
        />
      )}

      <Box display="flex" alignItems="center">
        {ownerableType === 'Channel' && (
          <Box mr={3}>
            <Toggle
              options={[
                {
                  label: 'All',
                  value: null,
                  width: 160,
                },
                {
                  label: t(`document.kind.waiver`),
                  value: 'waiver',
                  width: 160,
                },
                {
                  label: t(`document.kind.questionnaire`),
                  value: 'questionnaire',
                  width: 160,
                },
                {
                  label: t(`document.kind.contract`),
                  value: 'contract',
                  width: 160,
                },
              ]}
              value={filter?.kind}
              color="white"
              onChange={(value) => setFilter((prev) => ({ ...prev, kind: value }))}
              fontSize="large"
            />
          </Box>
        )}
        <Box flexGrow={1} mr={3}>
          <TextFieldRounded
            style={{ height: 32 }}
            placeholder={t('label.search.document')}
            value={filter?.text}
            onChange={(v) => setFilter((prev) => ({ ...prev, text: v }))}
          />
        </Box>
        <LoaderSm loading={loading} />
        <Box>
          <ButtonPrimary onClick={() => openModal()}>
            <Box display="flex" alignItems="flex-end">
              <Add size="small" />
              <Box alignItems="center">
                <Typography component="span" mr={1}>
                  {t('button.addDocument')}
                </Typography>
              </Box>
            </Box>
          </ButtonPrimary>
        </Box>
      </Box>

      <div className="tw-mt-2">
        <SFDataTable
          columns={columns}
          data={data || []}
          pagination
          highlightOnHover
          pointerOnHover
          // onRowClicked={(row) => viewClick(row)}
          noTableHead={false}
          noHeader
          showPaper
          paginationPerPage={30}
        />
      </div>

      <ModalDocumentViewer
        document={modalPreview.document}
        open={modalPreview.open}
        close={() => setModalPreview({ open: false })}
      />
    </Box>
  );
};

export default DocumentsBuilder;
